import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import type { FilterFieldMeta, FilterOperator } from '@aurora/contracts';
import { CURRENT_ACTOR_GROUPS_TOKEN, CURRENT_ACTOR_TOKEN, RELATIVE_DATE_PRESETS } from '@aurora/contracts';
import { cn } from '../../lib/cn';
import { useAgents, useDepartments, useGroups, useLocations } from '../../lib/directory';
import { Input, Select } from '../../components/ui';
import { PersonPicker } from '../../components/person-picker';
import { LIST_OPERATORS } from './list-filters';

/**
 * The value half of a filter condition, chosen by the field's type.
 *
 * Getting this right is most of what makes a filter builder usable. A person
 * field used to be a number box that wanted a database id nobody can see; "is
 * any of" was a native multi-select squeezed to one 28px row that needed
 * Ctrl-click; "is between" offered one input for a condition that needs two.
 * Each now gets the control its value actually takes.
 */

export const RELATIVE_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  tomorrow: 'Tomorrow',
  this_week: 'This week',
  last_week: 'Last week',
  next_week: 'Next week',
  this_month: 'This month',
  last_month: 'Last month',
  next_month: 'Next month',
  this_quarter: 'This quarter',
  last_quarter: 'Last quarter',
  this_year: 'This year',
  last_year: 'Last year',
};

/** References the builder can offer a picker for. Anything else has no lookup to offer. */
export const PICKABLE_REFERENCES = new Set(['agents', 'users', 'groups', 'departments', 'locations']);

// ---------------------------------------------------------------------------
// Names for ids
// ---------------------------------------------------------------------------

/**
 * People are looked up by search, not by id, so a requester chosen in the
 * builder — or seen on a row of the list — is remembered here to name the chip
 * that filters by them.
 */
const knownPeople = new Map<number, string>();

export function rememberPerson(id: number, name: string): void {
  if (name) knownPeople.set(id, name);
}

export type ReferenceLabeller = (references: string | undefined, value: unknown) => string | undefined;

/** Names for reference values, from the directory lists already in the cache. */
export function useReferenceLabeller(enabled: { agents?: boolean; groups?: boolean; departments?: boolean; locations?: boolean }): ReferenceLabeller {
  const agents = useAgents(enabled.agents === true);
  const groups = useGroups(enabled.groups === true);
  const departments = useDepartments(enabled.departments === true);
  const locations = useLocations(enabled.locations === true);

  return useMemo(() => {
    const maps: Record<string, Map<number, string>> = {
      agents: new Map((agents.data ?? []).map((row) => [row.id, row.name])),
      groups: new Map((groups.data ?? []).map((row) => [row.id, row.name])),
      departments: new Map((departments.data ?? []).map((row) => [row.id, row.name])),
      locations: new Map((locations.data ?? []).map((row) => [row.id, row.name])),
    };
    return (references, value) => {
      if (value === CURRENT_ACTOR_TOKEN) return 'me';
      if (value === CURRENT_ACTOR_GROUPS_TOKEN) return 'my groups';
      const id = Number(value);
      if (!references || !Number.isFinite(id)) return undefined;
      if (references === 'users' || references === 'agents') {
        return maps.agents?.get(id) ?? knownPeople.get(id);
      }
      return maps[references]?.get(id);
    };
  }, [agents.data, groups.data, departments.data, locations.data]);
}

// ---------------------------------------------------------------------------
// Multi-select
// ---------------------------------------------------------------------------

export interface Option {
  value: string;
  label: string;
}

/**
 * A list of checkboxes in a popover, searchable once it is long. Chosen values
 * show on the trigger by name.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  label,
  invalid,
  describedBy,
}: {
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  label: string;
  invalid?: boolean;
  describedBy?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const listId = useId();
  const chosen = new Set(value);
  const names = options.filter((option) => chosen.has(option.value)).map((option) => option.label);
  const shown = query
    ? options.filter((option) => option.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  const toggle = (option: string) =>
    onChange(chosen.has(option) ? value.filter((item) => item !== option) : [...value, option]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          // Named like every other value control ("Status value"), with what
          // is chosen after it, so the closed box still says what it holds.
          aria-label={`${label} value: ${names.length > 0 ? names.join(', ') : 'nothing chosen'}`}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          aria-expanded={open}
          className={cn(
            'flex h-7 max-w-64 min-w-36 items-center gap-1.5 rounded-control border border-line-strong bg-surface px-2 text-start text-mini text-ink',
            invalid && 'border-critical ring-1 ring-critical',
          )}
        >
          <span dir="auto" className={cn('min-w-0 flex-1 truncate', names.length === 0 && 'text-ink-subtle')}>
            {names.length > 0 ? names.join(', ') : 'Choose…'}
          </span>
          <ChevronsUpDown aria-hidden className="size-3 shrink-0 text-ink-subtle" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="animate-pop z-50 w-64 rounded-card border border-line bg-raised p-1 shadow-float"
        >
          {options.length > 8 && (
            <div className="relative mb-1">
              <Search aria-hidden className="pointer-events-none absolute start-2 top-1/2 size-3 -translate-y-1/2 text-ink-subtle" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Filter…"
                aria-label={`Search ${label}`}
                className="h-7 ps-6 text-mini"
              />
            </div>
          )}
          <div id={listId} role="group" aria-label={label} className="scrollbar-slim max-h-60 overflow-y-auto">
            {shown.map((option) => {
              const checked = chosen.has(option.value);
              return (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body text-ink hover:bg-sunken"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(option.value)}
                    className="size-3.5 accent-[var(--color-accent)]"
                  />
                  <span dir="auto" className="min-w-0 flex-1 truncate">
                    {option.label}
                  </span>
                  {checked && <Check aria-hidden className="size-3 text-accent" />}
                </label>
              );
            })}
            {shown.length === 0 && <p className="px-2 py-1.5 text-mini text-ink-subtle">Nothing matches.</p>}
          </div>
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-1 w-full rounded px-2 py-1 text-start text-mini text-ink-muted hover:bg-sunken"
            >
              Clear selection
            </button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------

export interface ValueEditorProps {
  meta: FilterFieldMeta;
  operator: FilterOperator;
  value: unknown;
  onChange: (value: unknown) => void;
  invalid?: boolean;
  describedBy?: string;
}

const CONTROL = 'h-7 text-mini';

export function ValueEditor(props: ValueEditorProps) {
  const { meta, operator } = props;
  const isList = LIST_OPERATORS.includes(operator);

  if (meta.type === 'boolean') return <BooleanValue {...props} />;
  if (meta.type === 'reference' && meta.references && PICKABLE_REFERENCES.has(meta.references)) {
    return meta.references === 'users' ? <PersonValue {...props} isList={isList} /> : <DirectoryValue {...props} isList={isList} />;
  }
  if (meta.choices && meta.choices.length > 0) {
    const options = meta.choices.map((choice) => ({ value: String(choice.value), label: choice.label }));
    return isList ? (
      <MultiSelect
        options={options}
        value={Array.isArray(props.value) ? props.value.map(String) : []}
        onChange={props.onChange}
        label={meta.label}
        invalid={props.invalid}
        {...(props.describedBy ? { describedBy: props.describedBy } : {})}
      />
    ) : (
      <Select
        size="sm"
        value={typeof props.value === 'string' || typeof props.value === 'number' ? String(props.value) : ''}
        onChange={(event) => props.onChange(event.target.value || undefined)}
        aria-label={`${meta.label} value`}
        aria-invalid={props.invalid || undefined}
        aria-describedby={props.describedBy}
        className="w-auto min-w-36"
      >
        <option value="">Choose…</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    );
  }
  if (meta.type === 'date' || meta.type === 'datetime') {
    return operator === 'between' ? <RangeValue {...props} kind="date" /> : <DateValue {...props} />;
  }
  if (meta.type === 'number' || meta.type === 'decimal' || meta.type === 'duration' || meta.type === 'reference') {
    return operator === 'between' ? <RangeValue {...props} kind="number" /> : <NumberValue {...props} />;
  }

  // Lists of text: tags, "is any of" on a text field.
  if (isList) return <ListTextValue {...props} />;

  // Free text.
  return (
    <Input
      type="text"
      value={typeof props.value === 'string' || typeof props.value === 'number' ? String(props.value) : ''}
      onChange={(event) => props.onChange(event.target.value === '' ? undefined : event.target.value)}
      placeholder="Value"
      aria-label={`${meta.label} value`}
      aria-invalid={props.invalid || undefined}
      aria-describedby={props.describedBy}
      className={cn(CONTROL, 'w-48')}
    />
  );
}

function splitList(raw: string): string[] {
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Comma-separated values, typed as text.
 *
 * What is typed is kept as typed. Redrawing the box from the parsed list threw
 * away the comma the moment it was typed — "printer," became "printer" — so a
 * second value could never be started.
 */
function ListTextValue({ meta, value, onChange, invalid, describedBy }: ValueEditorProps) {
  const joined = (Array.isArray(value) ? value.map(String) : []).join(', ');
  const [raw, setRaw] = useState(joined);

  // Follow a value changed from outside — a chip removed, a view applied —
  // without disturbing what is being typed when it already says the same.
  useEffect(() => {
    setRaw((current) => (splitList(current).join(', ') === joined ? current : joined));
  }, [joined]);

  return (
    <Input
      type="text"
      value={raw}
      onChange={(event) => {
        setRaw(event.target.value);
        const parsed = splitList(event.target.value);
        onChange(parsed.length > 0 ? parsed : undefined);
      }}
      placeholder="Separate with commas"
      aria-label={`${meta.label} value`}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className={cn(CONTROL, 'w-48')}
    />
  );
}

function BooleanValue({ meta, value, onChange, invalid, describedBy }: ValueEditorProps) {
  // Reads what is actually stored: an unset value says so instead of showing
  // "Yes" while holding nothing, which the old editor did.
  const current = value === true || value === 'true' ? 'true' : value === false || value === 'false' ? 'false' : '';
  return (
    <Select
      size="sm"
      value={current}
      onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value === 'true')}
      aria-label={`${meta.label} value`}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className="w-auto min-w-24"
    >
      {current === '' && <option value="">Choose…</option>}
      <option value="true">Yes</option>
      <option value="false">No</option>
    </Select>
  );
}

function NumberValue({ meta, value, onChange, invalid, describedBy }: ValueEditorProps) {
  const minutes = meta.type === 'duration';
  const shown =
    typeof value === 'number' || (typeof value === 'string' && value !== '')
      ? minutes
        ? String(Number(value) / 60)
        : String(value)
      : '';
  return (
    <span className="inline-flex items-center gap-1.5">
      <Input
        type="number"
        inputMode="decimal"
        value={shown}
        onChange={(event) => {
          const raw = event.target.value;
          if (raw === '') onChange(undefined);
          else onChange(minutes ? Number(raw) * 60 : Number(raw));
        }}
        aria-label={`${meta.label} value`}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className={cn(CONTROL, 'w-24')}
      />
      {minutes && <span className="text-mini text-ink-muted">minutes</span>}
    </span>
  );
}

/**
 * Two values for "is between". The server needs exactly two and says so with
 * a 400 otherwise; a single input could only ever produce that error.
 */
function RangeValue({
  meta,
  value,
  onChange,
  invalid,
  describedBy,
  kind,
}: ValueEditorProps & { kind: 'date' | 'number' }) {
  const pair = Array.isArray(value) && value.length === 2 ? value : ['', ''];
  const minutes = meta.type === 'duration';
  const read = (item: unknown) => {
    if (item === '' || item === undefined || item === null) return '';
    if (kind === 'number') return minutes ? String(Number(item) / 60) : String(item);
    return typeof item === 'string' ? item.slice(0, 10) : '';
  };
  const write = (index: 0 | 1, raw: string) => {
    const next = [...pair];
    next[index] = raw === '' ? '' : kind === 'number' ? (minutes ? Number(raw) * 60 : Number(raw)) : raw;
    onChange(next);
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Input
        type={kind === 'date' ? 'date' : 'number'}
        value={read(pair[0])}
        onChange={(event) => write(0, event.target.value)}
        aria-label={`${meta.label} from`}
        aria-invalid={(invalid && read(pair[0]) === '') || undefined}
        aria-describedby={describedBy}
        className={cn(CONTROL, kind === 'date' ? 'w-36' : 'w-24')}
      />
      <span className="text-mini text-ink-muted">and</span>
      <Input
        type={kind === 'date' ? 'date' : 'number'}
        value={read(pair[1])}
        onChange={(event) => write(1, event.target.value)}
        aria-label={`${meta.label} to`}
        aria-invalid={(invalid && read(pair[1]) === '') || undefined}
        aria-describedby={describedBy}
        className={cn(CONTROL, kind === 'date' ? 'w-36' : 'w-24')}
      />
      {minutes && <span className="text-mini text-ink-muted">minutes</span>}
    </span>
  );
}

export function describeRolling(value: unknown): string | null {
  if (!value || typeof value !== 'object' || !('rolling' in value)) return null;
  const rolling = (value as { rolling: { direction?: string; amount?: number; unit?: string } }).rolling;
  if (!rolling || typeof rolling.amount !== 'number' || !rolling.unit) return null;
  const unit = rolling.amount === 1 ? rolling.unit : `${rolling.unit}s`;
  return rolling.direction === 'next' ? `in the next ${rolling.amount} ${unit}` : `in the last ${rolling.amount} ${unit}`;
}

function DateValue({ meta, value, onChange, invalid, describedBy }: ValueEditorProps) {
  const relative =
    value && typeof value === 'object' && 'relative' in value ? String((value as { relative: string }).relative) : '';
  const rolling = describeRolling(value);
  const mode = relative || (rolling ? '__rolling' : '__absolute');
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Select
        size="sm"
        value={mode}
        onChange={(event) => {
          const next = event.target.value;
          if (next === '__absolute') onChange('');
          else if (next !== '__rolling') onChange({ relative: next });
        }}
        aria-label={`${meta.label} range`}
        aria-describedby={describedBy}
        className="w-auto min-w-36"
      >
        {RELATIVE_DATE_PRESETS.map((preset) => (
          <option key={preset} value={preset}>
            {RELATIVE_LABELS[preset] ?? preset}
          </option>
        ))}
        {/* A saved view's rolling window, kept as it is rather than lost. */}
        {rolling && <option value="__rolling">{rolling.charAt(0).toUpperCase() + rolling.slice(1)}</option>}
        <option value="__absolute">A specific date…</option>
      </Select>
      {mode === '__absolute' && (
        <Input
          type="date"
          value={typeof value === 'string' ? value.slice(0, 10) : ''}
          onChange={(event) => onChange(event.target.value)}
          aria-label={`${meta.label} date`}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={cn(CONTROL, 'w-36')}
        />
      )}
    </span>
  );
}

/**
 * Agents, groups, departments and locations: the directory lists are short,
 * so a select (or a checklist for "is any of") with the names in it. "Me" and
 * "My groups" are offered as the tokens the server resolves per viewer.
 */
function DirectoryValue({
  meta,
  value,
  onChange,
  invalid,
  describedBy,
  isList,
}: ValueEditorProps & { isList: boolean }) {
  const references = meta.references!;
  const agents = useAgents(references === 'agents');
  const groups = useGroups(references === 'groups');
  const departments = useDepartments(references === 'departments');
  const locations = useLocations(references === 'locations');

  const query =
    references === 'agents' ? agents : references === 'groups' ? groups : references === 'departments' ? departments : locations;
  const rows = (query.data ?? []) as Array<{ id: number; name: string }>;

  const options: Option[] = [
    ...(references === 'agents' ? [{ value: CURRENT_ACTOR_TOKEN, label: 'Me' }] : []),
    // "My groups" is a list, so it only makes sense where a list is accepted.
    ...(references === 'groups' && isList ? [{ value: CURRENT_ACTOR_GROUPS_TOKEN, label: 'My groups' }] : []),
    ...rows.map((row) => ({ value: String(row.id), label: row.name })),
  ];

  const toWire = (item: string): unknown =>
    item === CURRENT_ACTOR_TOKEN || item === CURRENT_ACTOR_GROUPS_TOKEN ? item : Number(item);

  if (query.isError) {
    return (
      <span role="alert" className="text-mini text-critical-ink">
        Couldn’t load {references}.
      </span>
    );
  }

  if (isList) {
    const current = Array.isArray(value)
      ? value.map(String)
      : value === CURRENT_ACTOR_GROUPS_TOKEN
        ? [CURRENT_ACTOR_GROUPS_TOKEN]
        : [];
    return (
      <MultiSelect
        options={options}
        value={current}
        onChange={(next) =>
          onChange(next.length === 1 && next[0] === CURRENT_ACTOR_GROUPS_TOKEN ? CURRENT_ACTOR_GROUPS_TOKEN : next.map(toWire))
        }
        label={meta.label}
        invalid={invalid}
        {...(describedBy ? { describedBy } : {})}
      />
    );
  }

  return (
    <Select
      size="sm"
      value={value === undefined || value === null || value === '' ? '' : String(value)}
      onChange={(event) => onChange(event.target.value === '' ? undefined : toWire(event.target.value))}
      aria-label={`${meta.label} value`}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className="w-auto min-w-40"
      disabled={query.isLoading}
    >
      <option value="">{query.isLoading ? 'Loading…' : 'Choose…'}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Select>
  );
}

/** Requesters: too many to list, so searched by name — one, or several as chips. */
function PersonValue({
  meta,
  value,
  onChange,
  invalid,
  describedBy,
  isList,
}: ValueEditorProps & { isList: boolean }) {
  const ids = (Array.isArray(value) ? value : value === undefined || value === null || value === '' ? [] : [value])
    .map(Number)
    .filter(Number.isFinite);

  if (!isList) {
    const id = ids[0];
    return (
      <span className="inline-flex w-56">
        <PersonPicker
          source="users"
          size="sm"
          value={id !== undefined ? { id, name: knownPeople.get(id) ?? `Person ${id}` } : null}
          onChange={(person) => {
            if (person) rememberPerson(person.id, person.name);
            onChange(person ? person.id : undefined);
          }}
          aria-label={`${meta.label} value`}
          aria-invalid={invalid}
          {...(describedBy ? { 'aria-describedby': describedBy } : {})}
        />
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {ids.map((id) => (
        <span key={id} className="inline-flex items-center gap-0.5 rounded-full border border-line bg-sunken ps-2 pe-0.5 text-mini text-ink">
          <span dir="auto">{knownPeople.get(id) ?? `Person ${id}`}</span>
          <button
            type="button"
            onClick={() => onChange(ids.filter((item) => item !== id))}
            aria-label={`Remove ${knownPeople.get(id) ?? `person ${id}`}`}
            className="rounded-full p-0.5 hover:bg-line"
          >
            <X aria-hidden className="size-2.5" />
          </button>
        </span>
      ))}
      <span className="inline-flex w-48">
        <PersonPicker
          source="users"
          size="sm"
          value={null}
          exclude={ids}
          placeholder={ids.length > 0 ? 'Add another…' : 'Search people…'}
          onChange={(person) => {
            if (!person) return;
            rememberPerson(person.id, person.name);
            onChange([...ids, person.id]);
          }}
          aria-label={`Add to ${meta.label}`}
          aria-invalid={invalid && ids.length === 0}
          {...(describedBy ? { 'aria-describedby': describedBy } : {})}
        />
      </span>
    </span>
  );
}
